/* eslint-disable */
import React, { useState } from 'react';
import { Modal, Box, Typography, Table, TableBody, TableRow, TableCell, Button, CircularProgress } from '@mui/material';
import axios from 'axios';

const SSNVerificationModal = ({ open, onClose, verificationData, onManualVerification, loading, onVerificationComplete }) => {
  const { 
    expectedData, 
    sparData, 
    reason = '', 
    status = '', 
    userId, 
    customerId,
    ssn, 
    isSsnValid,
    isPending = false // New flag for pending verification
  } = verificationData || {};

  const [verifying, setVerifying] = useState(false);
  const [localSparData, setLocalSparData] = useState(null);
  const [localStatus, setLocalStatus] = useState('');
  const [localReason, setLocalReason] = useState('');
  const [localIsSsnValid, setLocalIsSsnValid] = useState(null);
  const [verificationDone, setVerificationDone] = useState(false);

  const handleVerifySSN = async () => {
    const id = userId || customerId;
    if (!id || !ssn) return;

    setVerifying(true);
    try {
      const endpoint = userId 
        ? `/admin/invoice/ssn-verification/${userId}/${ssn}`
        : `/admin/ssn-verification/${customerId}/${ssn}`;
      
      const response = await axios.get(endpoint);
      
      if (response?.data?.statusCode === 200) {
        const data = response.data;
        setLocalSparData(data.details?.spar || {});
        setLocalStatus(data.status || (data.response === true ? 'success' : 'failed'));
        setLocalReason(data.reason || data.message || '');
        setLocalIsSsnValid(data.response === true);
        setVerificationDone(true);
        
        if (onVerificationComplete) {
          onVerificationComplete(data);
        }
      } else {
        setLocalStatus('failed');
        setLocalReason(response?.data?.message || 'Verification failed');
        setLocalIsSsnValid(false);
        setVerificationDone(true);
      }
    } catch (err) {
      const errorData = err?.response?.data;
      if (errorData?.status) {
        setLocalSparData(errorData.details?.spar || {});
        setLocalStatus(errorData.status);
        setLocalReason(errorData.reason || errorData.message || 'Verification failed');
        setLocalIsSsnValid(false);
        setVerificationDone(true);
        if (onVerificationComplete) {
          onVerificationComplete(errorData);
        }
      } else {
        setLocalStatus('failed');
        setLocalReason(err?.response?.data?.message || 'Failed to verify SSN');
        setLocalIsSsnValid(false);
        setVerificationDone(true);
      }
    } finally {
      setVerifying(false);
    }
  };

  // Reset local state when modal closes
  const handleClose = () => {
    setLocalSparData(null);
    setLocalStatus('');
    setLocalReason('');
    setLocalIsSsnValid(null);
    setVerificationDone(false);
    onClose();
  };

  // Ensure expectedData and sparData are always objects, even if null
  const expected = expectedData || {};
  // Use local spar data if verification was done in modal, otherwise use passed data
  const spar = verificationDone ? (localSparData || {}) : (sparData || {});
  const id = userId || customerId; // Support both userId and customerId
  
  // Determine current status and reason
  const currentStatus = verificationDone ? localStatus : status;
  const currentReason = verificationDone ? localReason : reason;
  const currentIsSsnValid = verificationDone ? localIsSsnValid : isSsnValid;
  const showPendingState = isPending && !verificationDone;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="ssn-verification-modal-title"
      aria-describedby="ssn-verification-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {verifying ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Verifying SSN...
            </Typography>
          </Box>
        ) : (
          <>
            <Typography 
              id="ssn-verification-modal-title" 
              variant="h5" 
              component="h2" 
              sx={{ 
                mb: 3, 
                color: showPendingState ? '#1976d2' : (currentStatus === 'failed' ? '#d32f2f' : '#2e7d32'), 
                fontWeight: 600 
              }}
            >
              {showPendingState ? '🔍 SSN Verification' : (currentStatus === 'failed' ? '⚠️ SSN Verification Failed' : '✓ SSN Verification Details')}
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 3, 
                p: 2, 
                bgcolor: showPendingState ? '#e3f2fd' : (currentStatus === 'failed' ? '#ffebee' : '#e8f5e9'), 
                borderRadius: 1, 
                color: showPendingState ? '#1565c0' : (currentStatus === 'failed' ? '#c62828' : '#2e7d32')
              }}
            >
              <strong>Status:</strong> {showPendingState ? 'Click "Verify SSN" to start verification' : (currentReason || 'Verification Successful')}
            </Typography>
          </>
        )}

        {!verifying && (
          <>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Expected Data Column */}
              <Box sx={{ flex: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  User Data (Sent)
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>First Name:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {expected.firstName || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Last Name:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {expected.lastName || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Date of Birth:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {expected.dateOfBirth || 'N/A'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* SPAR Data Column */}
              <Box sx={{ flex: 1, p: 2, borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  SPAR Data (Received)
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>First Name:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {spar.firstName || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Last Name:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {spar.lastName || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Date of Birth:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {spar.dateOfBirth || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>ID Number:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {spar.idNummer || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Gender:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {spar.kon || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Country:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {spar.country || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Address:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {spar.address || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>City:</TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {spar.postCity || 'N/A'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>

            {currentStatus === 'failed' && !showPendingState && (
              <Typography variant="body2" sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, fontSize: '0.875rem' }}>
                <strong>Note:</strong> Red boxes indicate mismatched values. Green boxes indicate matching values.
              </Typography>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              {showPendingState && (
                <Button
                  variant="contained"
                  onClick={handleVerifySSN}
                  sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
                >
                  Verify SSN
                </Button>
              )}
              {currentIsSsnValid === false && !showPendingState && id && ssn && onManualVerification && (
                <Button
                  variant="contained"
                  onClick={() => onManualVerification(id, ssn)}
                  disabled={loading}
                  sx={{ backgroundColor: '#f57c00', '&:hover': { backgroundColor: '#ef6c00' } }}
                >
                  Mark as verified
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{ backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' } }}
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default SSNVerificationModal;
